import { isValid, getDay, getHours, parseISO } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'

import {
  WEEKEND_DAYS,
  WORK_START_HOUR,
  WORK_END_HOUR,
} from 'constants/EntityConstants/OtherChargeEntity'
import { Weekday, CongestionZone } from 'generated/graphql'

const toValidDateLocal = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return isValid(value) ? value : null
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsedDate = new Date(value)
    return isValid(parsedDate) ? parsedDate : null
  }

  return null
}

const UK_TIMEZONE = 'Europe/London'

export const getChargeKey = (charge?: { id?: unknown; chargeKey?: unknown }): string =>
  String(charge?.id ?? charge?.chargeKey ?? '')

/// Returns true when the UK-local timestamp is outside 06:00 - 18:00 or falls on a Saturday/Sunday.
export const isOutOfHoursTimestamp = (value: unknown): boolean => {
  const date = toValidDateLocal(value)
  if (!date) {
    return false
  }

  const ukDate = utcToZonedTime(date, UK_TIMEZONE)
  const day = getDay(ukDate)
  if (WEEKEND_DAYS.includes(day)) {
    return true
  }

  const hour = getHours(ukDate)
  return hour < WORK_START_HOUR || hour >= WORK_END_HOUR
}

const toValidDateISO = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return isValid(value) ? value : null
  }

  if (typeof value === 'string') {
    try {
      const parsedDate = parseISO(value)
      return isValid(parsedDate) ? parsedDate : null
    } catch {
      return null
    }
  }

  return null
}

const normalizePostcode = (postcode: string): string => {
  return postcode?.trim()?.toUpperCase()?.replace(/\s/g, '') ?? ''
}

const getWeekdayName = (dayIndex: number): Weekday => {
  const days: Weekday[] = [
    Weekday.Sunday,
    Weekday.Monday,
    Weekday.Tuesday,
    Weekday.Wednesday,
    Weekday.Thursday,
    Weekday.Friday,
    Weekday.Saturday,
  ]
  return days[dayIndex] ?? Weekday.Sunday
}

const parseTimeString = (timeString: string): { hours: number; minutes: number } => {
  const parts = timeString.split(':')
  const hours = parts[0] ? parseInt(parts[0], 10) : 0
  const minutes = parts[1] ? parseInt(parts[1], 10) : 0

  return { hours, minutes }
}

const isTimeInZoneWindow = (
  date: Date,
  zone: CongestionZone,
  fromHour: number,
  fromMinute: number,
  toHour: number,
  toMinute: number,
): boolean => {
  const localTime = utcToZonedTime(date, zone.timeZone.name)
  const dayIndex = getDay(localTime)
  const hour = getHours(localTime)
  const minute = localTime.getMinutes()
  const weekdayName = getWeekdayName(dayIndex)

  if (!zone.weekdaysCollection?.includes(weekdayName)) {
    return false
  }

  const timeMinutes = hour * 60 + minute
  const fromMinutes = fromHour * 60 + fromMinute
  const toMinutes = toHour * 60 + toMinute

  return timeMinutes >= fromMinutes && timeMinutes < toMinutes
}

export const isZoneApplicableToAddress = (
  zone: CongestionZone,
  atTime: string,
  byTime: string,
): boolean => {
  const atDate = toValidDateISO(atTime)
  const byDate = toValidDateISO(byTime)

  if (!atDate && !byDate) {
    return false
  }

  if (zone.is24Hours) {
    return true
  }

  try {
    const { hours: fromHour, minutes: fromMinute } = parseTimeString(zone.durationFromString)
    const { hours: toHour, minutes: toMinute } = parseTimeString(zone.durationToString)

    if (atDate && isTimeInZoneWindow(atDate, zone, fromHour, fromMinute, toHour, toMinute)) {
      return true
    }

    if (byDate && isTimeInZoneWindow(byDate, zone, fromHour, fromMinute, toHour, toMinute)) {
      return true
    }
  } catch (error) {
    return false
  }

  return false
}

export const isPostcodeInZone = (
  postcode: string | null | undefined,
  zonePostcodes: string[],
): boolean => {
  if (!postcode) {
    return false
  }

  const normalized = normalizePostcode(postcode)
  return zonePostcodes.some((zonePostcode) =>
    normalized.startsWith(normalizePostcode(zonePostcode)),
  )
}
